import { createSlice, createAsyncThunk, type PayloadAction } from '@reduxjs/toolkit';
import { sectionsService } from '@/services/sectionsService';
import {
  sectionAssetToMediaItem,
} from '@/services/tabFeedService';
import { setMediaRowsFromLibrary } from '@/features/content/contentSlice';
import type {
  Section,
  SectionAssetCard,
  CreateSectionPayload,
  AssignSectionsPayload,
  ContentRow,
  MediaItem,
} from '@/types';

// ─── State ────────────────────────────────────────────────────────────────────

interface SectionAssetsState {
  items: SectionAssetCard[];
  total: number;
  offset: number;
}

interface SectionsState {
  /** Sections with preview assets — used for home screen rows */
  library: Section[];
  libraryLoading: boolean;
  libraryError: string | null;
  /** Flat list (no preview items) — used for sidebar / assign picker */
  sections: Section[];
  sectionsLoading: boolean;
  /** Paginated assets per section */
  sectionAssets: Record<string, SectionAssetsState>;
  sectionAssetsLoading: Record<string, boolean>;
  createLoading: boolean;
  createError: string | null;
  deleteLoading: boolean;
  assignLoading: boolean;
  /** Asset ID currently being assigned — drives AssignSectionModal open state */
  assignModalAssetId: string | null;
  /** Whether the CreateSectionModal is open */
  createModalOpen: boolean;
  /** Total video count from library API */
  totalVideoCount: number;
  /** Total video size in bytes from library API */
  totalVideoSizeBytes: number;
  /** Total duration in ms from library API */
  totalDurationMs: number | null;
}

const initialState: SectionsState = {
  library: [],
  libraryLoading: false,
  libraryError: null,
  sections: [],
  sectionsLoading: false,
  sectionAssets: {},
  sectionAssetsLoading: {},
  createLoading: false,
  createError: null,
  deleteLoading: false,
  assignLoading: false,
  assignModalAssetId: null,
  createModalOpen: false,
  totalVideoCount: 0,
  totalVideoSizeBytes: 0,
  totalDurationMs: null,
};

// ─── Helpers ──────────────────────────────────────────────────────────────────

/** Convert a v3 Section → ContentRow for the home screen */
export function sectionToContentRow(section: Section): ContentRow {
  const items = section.assets.map((a, i) => sectionAssetToMediaItem(a, i)) as MediaItem[];
  return {
    id: section.id,
    title: section.label.charAt(0).toUpperCase() + section.label.slice(1),
    subtitle: `${section.asset_count} item${section.asset_count !== 1 ? 's' : ''}`,
    type: 'default',
    items,
    isUserCustom: section.type === 'user_custom',
    isDynamic: section.is_dynamic,
  };
}

// ─── Thunks ───────────────────────────────────────────────────────────────────

/** Fetches the full library grid and syncs it into content.mediaRows */
export const fetchLibrary = createAsyncThunk(
  'sections/fetchLibrary',
  async (_, { dispatch, rejectWithValue }) => {
    try {
      const res = await sectionsService.getLibrary();
      const rows: ContentRow[] = res.data.sections.map(sectionToContentRow);
      dispatch(setMediaRowsFromLibrary(rows));
      return {
        sections: res.data.sections,
        totalVideoCount: res.data.total_video_count,
        totalVideoSizeBytes: res.data.total_video_size_bytes,
        totalDurationMs: res.data.total_duration_ms ?? null,
      };
    } catch {
      return rejectWithValue('Failed to load library');
    }
  }
);

/** Fetches the flat section list for sidebar / assign picker */
export const fetchSectionList = createAsyncThunk(
  'sections/fetchSectionList',
  async (_, { rejectWithValue }) => {
    try {
      const res = await sectionsService.getSections();
      return res.data.sections;
    } catch {
      return rejectWithValue('Failed to load sections');
    }
  }
);

/** Fetches (and optionally appends) paginated assets for a section */
export const fetchSectionAssets = createAsyncThunk(
  'sections/fetchSectionAssets',
  async (
    { id, limit = 24, offset = 0 }: { id: string; limit?: number; offset?: number },
    { rejectWithValue }
  ) => {
    try {
      const res = await sectionsService.getSectionAssets(id, limit, offset);
      return { id, ...res.data };
    } catch {
      return rejectWithValue(id);
    }
  }
);

/** Creates a user_custom section */
export const createSection = createAsyncThunk(
  'sections/createSection',
  async (payload: CreateSectionPayload, { dispatch, rejectWithValue }) => {
    try {
      const res = await sectionsService.createSection(payload);
      // Refresh library so the new section row appears at home
      dispatch(fetchLibrary());
      return res.data;
    } catch (err: unknown) {
      const status = (err as { response?: { status?: number } })?.response?.status;
      if (status === 409) return rejectWithValue('A collection with that name already exists.');
      return rejectWithValue('Failed to create collection.');
    }
  }
);

/** Deletes a user_custom section */
export const deleteSection = createAsyncThunk(
  'sections/deleteSection',
  async (id: string, { dispatch, rejectWithValue }) => {
    try {
      await sectionsService.deleteSection(id);
      // Refresh library so the deleted row disappears
      dispatch(fetchLibrary());
      return id;
    } catch {
      return rejectWithValue('Failed to delete collection.');
    }
  }
);

/** Assigns an asset to one or more sections */
export const assignAssetToSections = createAsyncThunk(
  'sections/assignAssetToSections',
  async (
    { assetId, payload }: { assetId: string; payload: AssignSectionsPayload },
    { dispatch, rejectWithValue }
  ) => {
    try {
      const res = await sectionsService.assignAssetToSections(assetId, payload);
      // Refresh library so the newly assigned asset appears in the section row
      dispatch(fetchLibrary());
      return res.data;
    } catch {
      return rejectWithValue('Failed to assign asset to collections.');
    }
  }
);

/** Removes an asset from a section */
export const removeAssetFromSection = createAsyncThunk(
  'sections/removeAssetFromSection',
  async (
    { assetId, sectionId }: { assetId: string; sectionId: string },
    { dispatch, getState, rejectWithValue }
  ) => {
    try {
      await sectionsService.removeAssetFromSection(assetId, sectionId);
      // Optimistically recompute home screen rows without waiting for a refetch
      const { sections } = getState() as { sections: SectionsState };
      const updatedLibrary = sections.library.map((sec) =>
        sec.id === sectionId
          ? {
              ...sec,
              assets: sec.assets.filter((a) => a.asset_id !== assetId),
              asset_count: Math.max(0, sec.asset_count - 1),
            }
          : sec
      );
      dispatch(setMediaRowsFromLibrary(updatedLibrary.map(sectionToContentRow)));
      return { assetId, sectionId };
    } catch {
      return rejectWithValue('Failed to remove asset from collection.');
    }
  }
);

// ─── Slice ────────────────────────────────────────────────────────────────────

const sectionsSlice = createSlice({
  name: 'sections',
  initialState,
  reducers: {
    openAssignModal(state, action: PayloadAction<string>) {
      state.assignModalAssetId = action.payload;
    },
    closeAssignModal(state) {
      state.assignModalAssetId = null;
    },
    openCreateModal(state) {
      state.createModalOpen = true;
      state.createError = null;
    },
    closeCreateModal(state) {
      state.createModalOpen = false;
      state.createError = null;
    },
  },
  extraReducers: (builder) => {
    // ── fetchLibrary ─────────────────────────────────────────────────────────
    builder
      .addCase(fetchLibrary.pending, (state) => {
        state.libraryLoading = true;
        state.libraryError = null;
      })
      .addCase(fetchLibrary.fulfilled, (state, action) => {
        state.libraryLoading = false;
        const payload = action.payload as { sections: Section[]; totalVideoCount: number; totalVideoSizeBytes: number; totalDurationMs: number | null };
        state.library = payload.sections;
        state.totalVideoCount = payload.totalVideoCount;
        state.totalVideoSizeBytes = payload.totalVideoSizeBytes;
        state.totalDurationMs = payload.totalDurationMs ?? null;
      })
      .addCase(fetchLibrary.rejected, (state, action) => {
        state.libraryLoading = false;
        state.libraryError = action.payload as string;
      });

    // ── fetchSectionList ─────────────────────────────────────────────────────
    builder
      .addCase(fetchSectionList.pending, (state) => {
        state.sectionsLoading = true;
      })
      .addCase(fetchSectionList.fulfilled, (state, action) => {
        state.sectionsLoading = false;
        state.sections = action.payload as Section[];
      })
      .addCase(fetchSectionList.rejected, (state) => {
        state.sectionsLoading = false;
      });

    // ── fetchSectionAssets ───────────────────────────────────────────────────
    builder
      .addCase(fetchSectionAssets.pending, (state, action) => {
        const id = action.meta.arg.id;
        state.sectionAssetsLoading[id] = true;
      })
      .addCase(fetchSectionAssets.fulfilled, (state, action) => {
        const { id, assets, total, offset } = action.payload as {
          id: string;
          assets: SectionAssetCard[];
          total: number;
          limit: number;
          offset: number;
        };
        state.sectionAssetsLoading[id] = false;
        if (offset === 0) {
          state.sectionAssets[id] = { items: assets, total, offset };
        } else {
          // Append for "Load More" pagination
          const existing = state.sectionAssets[id];
          if (existing) {
            existing.items = [...existing.items, ...assets];
            existing.total = total;
            existing.offset = offset;
          } else {
            state.sectionAssets[id] = { items: assets, total, offset };
          }
        }
      })
      .addCase(fetchSectionAssets.rejected, (state, action) => {
        const id = action.payload as string;
        state.sectionAssetsLoading[id] = false;
      });

    // ── createSection ────────────────────────────────────────────────────────
    builder
      .addCase(createSection.pending, (state) => {
        state.createLoading = true;
        state.createError = null;
      })
      .addCase(createSection.fulfilled, (state, action) => {
        state.createLoading = false;
        state.createModalOpen = false;
        // Add to the picker list too
        state.sections.push(action.payload as Section);
      })
      .addCase(createSection.rejected, (state, action) => {
        state.createLoading = false;
        state.createError = action.payload as string;
      });

    // ── deleteSection ────────────────────────────────────────────────────────
    builder
      .addCase(deleteSection.pending, (state) => {
        state.deleteLoading = true;
      })
      .addCase(deleteSection.fulfilled, (state, action) => {
        state.deleteLoading = false;
        const id = action.payload as string;
        state.library = state.library.filter((s) => s.id !== id);
        state.sections = state.sections.filter((s) => s.id !== id);
        delete state.sectionAssets[id];
      })
      .addCase(deleteSection.rejected, (state) => {
        state.deleteLoading = false;
      });

    // ── assignAssetToSections ────────────────────────────────────────────────
    builder
      .addCase(assignAssetToSections.pending, (state) => {
        state.assignLoading = true;
      })
      .addCase(assignAssetToSections.fulfilled, (state) => {
        state.assignLoading = false;
        state.assignModalAssetId = null;
      })
      .addCase(assignAssetToSections.rejected, (state) => {
        state.assignLoading = false;
      });

    // ── removeAssetFromSection ───────────────────────────────────────────────
    builder
      .addCase(removeAssetFromSection.fulfilled, (state, action) => {
        const { assetId, sectionId } = action.payload as { assetId: string; sectionId: string };
        // Update detail-page bucket
        const bucket = state.sectionAssets[sectionId];
        if (bucket) {
          bucket.items = bucket.items.filter((a) => a.asset_id !== assetId);
          bucket.total = Math.max(0, bucket.total - 1);
        }
        // Keep library in sync so future recomputes are accurate
        const libSection = state.library.find((s) => s.id === sectionId);
        if (libSection) {
          libSection.assets = libSection.assets.filter((a) => a.asset_id !== assetId);
          libSection.asset_count = Math.max(0, libSection.asset_count - 1);
        }
      });
  },
});

export const { openAssignModal, closeAssignModal, openCreateModal, closeCreateModal } =
  sectionsSlice.actions;
export default sectionsSlice.reducer;
